import { asyncify, mapLimit } from 'async';

import { UserId, Uuid } from '../../models/Base';
import {
  GameControllerIntermediate,
  GameControllerIntermediateGameItem,
  GameInfo,
} from '../../models/GameControllerStore';
import {
  ChallengeRecord,
  ChallengeRecordMode,
  ChallengeRecordType,
  GameRecord,
} from '../../models/RecordStore';
import { useGameStore } from '../../stores/GameStore';
import { useRecordStore } from '../../stores/RecordStore';

import { getGameScope } from './utils';

export const fetchGames = async (
  {
    classroomId,
    gameSetId,
    postId,
    reportCase,
  }: {
    classroomId: string;
    gameSetId: string;
    postId?: string;
    reportCase?: string;
  },
  gameItems: GameControllerIntermediate['gameItems'],
): Promise<(GameInfo | undefined)[]> => {
  const { getGame, getGameStatistics, fetchGameStatistics, fetchGame } =
    useGameStore.getState();

  return mapLimit(
    gameItems,
    100,
    asyncify(
      async ({
        gameCollectionId,
        game: { gameId, hash },
        category,
        fake,
      }: GameControllerIntermediateGameItem): Promise<GameInfo | undefined> => {
        const game = getGame(gameId);
        const gameStatistics = getGameStatistics(gameId);
        if (game) {
          // gameStatistics is not a must
          return {
            hash,
            category,
            fake,
            gameStatistics,
            ...game.toJSON(),
          };
        }

        const scope = getGameScope({
          classroom: classroomId,
          gameSet: gameSetId,
          gameCollection: gameCollectionId,
          post: postId,
          reportCase,
        });
        if (!game) {
          await fetchGame({
            fieldName: 'gamePlay',
            gameCollectionId,
            gameId,
            hash,
            scope,
          });
        }
        if (!gameStatistics) {
          await fetchGameStatistics({
            fieldName: 'gamePlay',
            gameCollectionId,
            gameId,
            hash,
            scope,
          });
        }
        return;
      },
    ),
  );
};

export const fetchGameRecords = async (
  {
    classroomId,
    gameSetId,
    gameId,
    userId,
  }: { classroomId: Uuid; gameSetId: Uuid; gameId: Uuid; userId: UserId },
  filter: (value: GameRecord, index: number, array: GameRecord[]) => unknown,
  _forceFetch = false,
): Promise<GameRecord[] | undefined> => {
  const { getGameRecords, fetchGameRecords: fetchRecords } =
    useRecordStore.getState();

  await fetchRecords(
    {
      fieldName: 'gamePlay',
      classroomId,
      playedBy: userId,
      gameSetId,
      gameId,
    },
    undefined,
  );

  const gameRecords = getGameRecords('gamePlay');
  if (gameRecords) {
    return gameRecords.filter(filter);
  }
  return;
};

export const fetchChallengeRecords = async (
  { classroomId, gameSetId }: { classroomId: Uuid; gameSetId: Uuid },
  type: ChallengeRecordType,
  mode: ChallengeRecordMode = ChallengeRecordMode.Simple,
): Promise<ChallengeRecord[] | undefined> => {
  const { getChallengeRecords, fetchChallengeRecords: fetchRecords } =
    useRecordStore.getState();

  await fetchRecords(
    { fieldName: 'gamePlay', classroomId, gameSetId },
    undefined,
  );

  const challengeRecords = getChallengeRecords('gamePlay');
  if (challengeRecords) {
    return challengeRecords.filter((r) => r.type === type && r.mode === mode);
  }
  return;
};

// export const fetchRevisionSchedule = async (
//   {
//     classroomId,
//     userId,
//     gameId,
//   }: {
//     classroomId: Uuid;
//     userId: UserId;
//     gameId: Uuid;
//   },
//   forceFetch = false,
// ): Promise<RevisionSchedule | null | undefined> => {
//   const { getRevisionSchedules, getRevisionSchedule, fetchRevisionSchedules } =
//     useRecordStore.getState();

//   await fetchRevisionSchedules(classroomId, forceFetch);

//   const revisionSchedules = getRevisionSchedules(classroomId); // use to check fetched
//   const revisionSchedule = getRevisionSchedule(gameId, userId, classroomId);
//   if (revisionSchedules !== undefined) {
//     return revisionSchedule === undefined ? null : revisionSchedule;
//   }
//   return;
// };

export const fetchGameOpponents = async ({
  classroomId,
  gameSetId,
  gameId,
}: {
  classroomId: Uuid;
  gameSetId: Uuid;
  gameId: Uuid;
}) => {
  return useRecordStore
    .getState()
    .fetchGameOpponents(classroomId, gameSetId, gameId, 1);
};
